<template>
  <div class="entity-list">
    <ViewHeader title="Entity list per Financial department">
      <CButton
        color="secondary"
        class="ra-button"
        @click="() => $downloadFile({
          url: ENTITY_ENDPOINTS.model_export,
        })"
      >
        Export entities
      </CButton>
      <template v-if="ActiveUser.isAdministrator()">
        <CButton
          color="primary"
          class="ra-button"
          :disabled="!selectedEntities.length"
          @click="() => triggerEditModal()"
        >
          Edit selection
        </CButton>
        <CButton
          color="primary"
          class="ra-button"
          data-cy="create-entity"
          @click="() => triggerAddModal()"
        >
          Create entity
        </CButton>
      </template>
    </ViewHeader>

    <div class="d-flex gap-5 mt-2">
      <tableComponent
        class="entity-table"
        :key="`entity-table_${refreshTable}`"
        :data="allEntities"
        :columns="tableColumns"
        :filter-exclude-columns="['actions']"
        :sort-exclude-columns="['actions', '_id']"
        ref="entities_table"
        :headings="{
          code: 'Entity code',
          group: 'DF',
          ...headings,
        }"
        @row-selected="rowSelected"
        keep-id
        :should-allow-row-selection="ActiveUser.isAdministrator()"
      >
        <template #row-selection-button>
          <div />
        </template>
        <template
          v-for="category in possibleOptions.categories"
          #[category]="{ row }"
        >
          <div>
            {{ typeof(row[category]) === 'string' ? row[category] : (row[category] || []).join(", ") }}
          </div>
        </template>
        <template #actions="{ row }">
          <div class="d-flex gap-2">
            <CButton
              class="ra-icon-button"
              v-c-tooltip="'Delete entity'"
              @click="() => removeEntity(row._id)"
              data-cy="delete-entity"
            >
              <Icon name="trash"/>
            </CButton>
            <CButton
              class="ra-icon-button"
              v-c-tooltip="'Edit entity'"
              @click="() => triggerEditModal(row)"
              data-cy="edit-entity"
            >
              <Icon name="pencil"/>
            </CButton>
          </div>
        </template>
      </tableComponent>
      <SideModal
        v-if="showEditModal"
        :isVisible="showEditModal"
        :wrapper-style="{ width: '30%' }"
        :closeModal="closeEditModal"
        data-cy="create-entity-modal"
      >
        <template #modal-header>
          <h3>{{ isCreate ? "Create" : "Edit" }} entity settings</h3>
        </template>
        <template #modal-content>
          <div class="px-3 py-2">
            <template v-if="isCreate">
              <div v-for="formField in inputFields">
                <InputComponent
                  :key="formField.name"
                  :formField="formField"
                  @select="selectHandler"
                  @remove="removeHandler"
                />
              </div>
            </template>
            <div
              v-for="formField in Object.values(formFields)"
              :key="formField.name"
            >
              <InputComponent
                :key="formField.name"
                :formField="formField"
                @select="selectHandler"
                @remove="removeHandler"
              />
            </div>
            <div
              class="d-flex justify-content-end gap-5 align-items-center mt-3"
            >
              <CButton
                color="primary"
                class="ra-button"
                @click="closeEditModal"
              >
                Cancel
              </CButton>
              <CButton
                color="primary"
                class="ra-button"
                data-cy="save-entity"
                @click="
                  () =>
                    isCreate ? addEntity() : handleEntitiesCategoriesEdit()
                "
              >
                <span v-if="isCreate"> Create entity </span>
                <span v-else> Edit entities </span>
              </CButton>
            </div>
          </div>
        </template>
      </SideModal>
    </div>
  </div>
</template>

<script>
import { EntitiesService, ENTITY_ENDPOINTS } from "@/services/entity";
import { UsersService } from "@/services/users";
import { ActiveUser } from "@/services/user";
import { UsersMixin } from "@/mixins/users";

import InputComponent from "@/components/InputComponent";

const defaultFormField = {
  name: "",
  type: "multiselect",
  placeholder: "Please assign to ",
  value: [],
  options: [],
};

const initInputFields = {
  code: {
    name: "code",
    label: "Entity code",
    type: "text",
    value: "",
  },
  group: {
    ...defaultFormField,
    placeholder: "Please select department",
    name: "group",
    label: "Financial department",
  },
};

export default {
  name: "EntityList",
  mixins: [UsersMixin],
  components: { InputComponent },
  data() {
    return {
      inputFieldsGroupOptions: null,
      ActiveUser,
      ENTITY_ENDPOINTS,
      refreshTable: 0,
      possibleOptions: {
        categories: [],
      },
      financialManagementScopes: [],
      selectedEntities: [],
      changedFields: [],
      formFields: {},
      inputFields: {},
      headings: {},
      showEditModal: false,
      isCreate: false,
      allEntities: [],
    };
  },
  computed: {
    tableColumns(){
      let columns = ['code', 'group', ...this.possibleOptions.categories.map(cat => `${cat}_str`)];
      const comptaIndex = columns.indexOf("compta")
      if (comptaIndex >= 0) columns.splice(comptaIndex,1)
      if (ActiveUser.isAdministrator()) columns.push('actions')
      return columns
    },
    createPayload() {
      const payload = {};
      Object.values(this.inputFields).forEach((field) => {
        payload[field.name] = field.value;
      });
      return payload;
    },
    payload() {
      const payload = {};
      Object.values(this.formFields).forEach((field) => {
        payload[field.name] = field.value;
      });
      return payload;
    },
    selectedFinancialGroups() {
      return this.selectedEntities.map((entity) => entity.group);
    },
  },
  async mounted() {
    this.setInputFields();
    this.inputFieldsGroupOptions = (
      await UsersService.getRoles()
    ).financial_role.map((role) => {
      return { value: role, name: role };
    });
    this.inputFields.group.options = this.inputFieldsGroupOptions;
    const possibleOptions = await EntitiesService.getOptions();

    Object.values(possibleOptions.categories).forEach((field) => {
      this.headings[field["field_name"]] = field["label"];
    });
    const entityCategories = Object.keys(this.headings);
    this.possibleOptions.categories = entityCategories;

    entityCategories.forEach(cat => {
      this.headings[`${cat}_str`] = this.headings[cat]
    });

    this.createFormFields();
    await this.fetchEntities();
  },
  methods: {
    async fetchEntities() {
      this.allEntities = await EntitiesService.getAllEntities();
    },
    setInputFields() {
      this.inputFields = JSON.parse(JSON.stringify(initInputFields));
      this.inputFields.group.options = this.inputFieldsGroupOptions
    },
    selectHandler(item, fieldName) {
      if(Object.keys(this.inputFields).includes(fieldName)) return this.selectItemSelect(item, fieldName, this.inputFields);
      return this.selectItemSelect(item, fieldName, this.formFields)
    },
    selectItemSelect(item, fieldName, fields) {
      if (fields[fieldName].value.includes(item)) return this.removeHandler(item, fieldName, fields);
      this.changedFields.push(fieldName);
    },
    removeHandler(item, fieldName) {
      if(Object.keys(this.inputFields).includes(fieldName)) return this.removeItemSelect(fieldName);
      return this.removeItemSelect(fieldName)
    },
    removeItemSelect(item, fieldName) {
      this.changedFields.push(fieldName);
    },
    async createFormFields() {
      const members = await this.populateMembers(true);
      this.possibleOptions.categories.forEach((category) => {
        const field = {
          ...defaultFormField,
          name: category,
          label: this.headings[category],
          options: [...members],
        };
        this.formFields[category] = JSON.parse(JSON.stringify(field));
      });
    },
    rowSelected(selected) {
      this.selectedEntities = selected;
    },
    triggerAddModal() {
      this.isCreate = true;
      this.triggerEditModal();
    },
    triggerEditModal(entity=undefined) {
      if (entity) {
        this.selectedEntities.push(entity)
      }
      this.showEditModal = !this.showEditModal;
      if (this.showEditModal) this.createFormFields();
    },
    closeEditModal(reset=false) {
      this.showEditModal = !this.showEditModal;
    },
    async entitySaveCallback() {
      await this.fetchEntities();
      this.createFormFields();
      this.setInputFields();
      this.isCreate = false;
      this.showEditModal = false;
      this.changedFields = [];
      this.refreshTable += 1;
      this.selectedEntities = [];
    },
    async handleEntitiesCategoriesEdit() {
      await EntitiesService.handleEntitiesCategoriesEdit(
        this.selectedEntities.map((entity) => entity._id),
        this.payload,
        this.changedFields
      );
      this.entitySaveCallback();
    },
    async addEntity() {
       const {res, data} = await EntitiesService.editOrCreateEntity(null, {
        ...this.payload,
        ...this.createPayload,
      });
      if (res.status === 200) this.entitySaveCallback();
      else alert(data.detail)
    },
    async removeEntity(entityId) {
      await EntitiesService.deleteEntity(entityId);
      this.entitySaveCallback();
    },
  },
};
</script>

<style lang="scss">
.entity-table {
  .table-container {
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
    height: 400px;
    overflow: auto;
  }
  table {
    width: 100%;
    margin-bottom: 1em;
  }
  thead {
    position: sticky;
    top: 0;
    z-index: 1;
  }
  thead th {
    background-color: white;
    text-align: left;
    padding: 0.5em 1em;
  }
}
.ra-table .table.VueTables__table tbody tr.VueTables__row td {
  min-width: 220px;
}
.buttons-row {
  display: none !important;
}
</style>
